import { createModel } from '@rematch/core';
import type { RootModel } from '.';
import { MyDashboardTypes } from '../types/my-dashboard.types';
import { AlertTypes } from '../types/alert.types';

export interface ScopeItem {
    id: string;
    name: string;
}

export interface MyDashboardScope {
    role: string;
    groups: ScopeItem[];
    teams: ScopeItem[];
}

export interface MyDashboardStoreProps {
    summary: MyDashboardTypes.Summary | null;
    summaryError: boolean;
    drillDown: MyDashboardTypes.DrillDownState;
    drillDownError: boolean;
    activeEvents: AlertTypes.Model[];
    /** Epoch ms of the last successful active-events fetch. 0 = never. */
    activeEventsLastFetch: number;
    /** True when the most recent active-events fetch failed. Distinct from
     *  lastFetch=0 so a first-load failure is surfaced as an error rather
     *  than the indistinguishable "no events yet" state. */
    activeEventsError: boolean;
    scope: MyDashboardScope | null;
    scopeError: boolean;
    loading: {
        summary: boolean;
        drillDown: boolean;
        activeEvents: boolean;
        scope: boolean;
    };
}

export const myDashboardInitialState: MyDashboardStoreProps = {
    summary: null,
    summaryError: false,
    drillDown: {
        metricKey: null,
        items: [],
        page: 0,
        perPage: 25,
        total: 0,
    },
    drillDownError: false,
    activeEvents: [],
    activeEventsLastFetch: 0,
    activeEventsError: false,
    scope: null,
    scopeError: false,
    loading: {
        summary: false,
        drillDown: false,
        activeEvents: false,
        scope: false,
    },
};

export const myDashboard = createModel<RootModel>()({
    state: myDashboardInitialState,
    reducers: {
        toggleLoading(state, key: keyof MyDashboardStoreProps['loading'], value: boolean) {
            state.loading[key] = value;
        },
        setSummary(state, summary: MyDashboardTypes.Summary) {
            state.summary = summary;
        },
        setSummaryError(state, hasError: boolean) {
            state.summaryError = hasError;
        },
        setDrillDown(state, payload: MyDashboardTypes.DrillDownState) {
            state.drillDown = payload;
        },
        setDrillDownError(state, hasError: boolean) {
            state.drillDownError = hasError;
        },
        clearDrillDown(state) {
            state.drillDown = myDashboardInitialState.drillDown;
            state.drillDownError = false;
        },
        setActiveEvents(state, events: AlertTypes.Model[]) {
            state.activeEvents = events;
        },
        setActiveEventsLastFetch(state, at: number) {
            state.activeEventsLastFetch = at;
        },
        setActiveEventsError(state, hasError: boolean) {
            state.activeEventsError = hasError;
        },
        setScope(state, scope: MyDashboardScope) {
            state.scope = scope;
        },
        setScopeError(state, hasError: boolean) {
            state.scopeError = hasError;
        },
    },
});
